/**
 * Dirty Sheet Tracker
 *
 * Tracks which sheets have been modified by tool calls during a turn.
 * Used to skip unchanged sheets in the "after" snapshot capture,
 * copying clean sheets from the "before" snapshot instead.
 */

import { parseAddressWithSheet } from '../excel-utils'

let dirtySheets = new Set<string>()
let allSheetsDirty = false

/**
 * Mark a sheet as dirty (modified by a tool).
 * Pass null to mark the active/default sheet.
 */
export function markSheetDirty(sheetName: string | null): void {
  if (sheetName) {
    dirtySheets.add(sheetName.toLowerCase())
  } else {
    // null means active sheet — mark all dirty as fallback
    allSheetsDirty = true
  }
}

/**
 * Mark all sheets as dirty (e.g., for executeCode which can modify anything).
 */
export function markAllSheetsDirty(): void {
  allSheetsDirty = true
}

/**
 * Mark sheets as dirty based on a tool call's address arguments.
 * Parses sheet names from addresses like "Sheet1!A1:B10".
 */
export function markDirtyFromAddresses(addresses: string[]): void {
  for (const address of addresses) {
    const { sheetName } = parseAddressWithSheet(address)
    markSheetDirty(sheetName)
  }
}

/**
 * Check if a sheet is dirty.
 */
export function isSheetDirty(sheetName: string): boolean {
  if (allSheetsDirty) return true
  return dirtySheets.has(sheetName.toLowerCase())
}

/**
 * Check if all sheets are marked dirty.
 */
export function areAllSheetsDirty(): boolean {
  return allSheetsDirty
}

/**
 * Get the set of dirty sheet names (lowercase).
 */
export function getDirtySheets(): ReadonlySet<string> {
  return dirtySheets
}

/**
 * Reset dirty tracking for a new turn.
 */
export function resetDirtyTracker(): void {
  dirtySheets = new Set<string>()
  allSheetsDirty = false
}
